import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { QuickPromptBar } from '../QuickPromptBar';
import { useWebSocket } from '../../contexts/WebSocketContext';
import StageModelsWidget from '../common/StageModelsWidget';
import Internals from '../common/Internals';
import {
  AgentErrorBanner,
  AgentLoadError,
  AgentStatusChip,
} from '../common/AgentErrorBanner';
import ContextUsageChip from '../ContextUsageChip';
import type {
  PageBuilderChatMessage,
  PageBuilderChatResponse,
  WebSocketEvent,
} from '../../types';

// ============================================================================
// PageBuilderChatPane — slide-out "Concierge" chat on the Page Builder page.
//
// Near-clone of the discover-keywords chat (DiscoveryPane's right pane). The
// concierge agent ORCHESTRATES the existing build phases: a user message can
// trigger phase re-runs (research / angle / outline / write / ground) that
// run asynchronously and update the EXISTING phase stepper via the normal
// page_build / synthesize_research / angle_generated events. This pane is the
// "talk to the builder" surface; it only owns the chat lifecycle, which flows
// via `page_builder_chat` websocket events.
// ============================================================================

interface PageBuilderChatPaneProps {
  pageId: number;
  open: boolean;
  onClose: () => void;
}

export default function PageBuilderChatPane({
  pageId,
  open,
  onClose,
}: PageBuilderChatPaneProps) {
  const { subscribe } = useWebSocket();

  const [chat, setChat] = useState<PageBuilderChatResponse | null>(null);
  const [chatLoadError, setChatLoadError] = useState<string | null>(null);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Which window of static quick-prompts to show; the ↻ button rotates it.
  const [quickPromptPage, setQuickPromptPage] = useState(0);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);

  useEscapeKey(open, onClose);

  const refreshChat = useCallback(async () => {
    if (!pageId) return;
    try {
      const next = await api.getPageBuilderChat(pageId);
      setChat(next);
      setChatLoadError(null);
    } catch (err) {
      console.error('Failed to load page-builder chat:', err);
      setChatLoadError(
        err instanceof Error ? err.message : 'Failed to load chat.',
      );
    }
  }, [pageId]);

  // Load when the pane opens (or when the page changes while open). We
  // hold off on fetching while closed to avoid pulling chat history the
  // user hasn't asked to see.
  useEffect(() => {
    if (open && pageId) refreshChat();
  }, [open, pageId, refreshChat]);

  // Chat-turn lifecycle. Mirrors DiscoveryPane's discover_keywords_chat
  // handling: on "started" the GET below will report status="thinking";
  // on "replied" / "failed" we re-fetch to flip status + pull the new
  // messages. The phase stepper updates itself off its own
  // page_build / synthesize_research / angle_generated events — we don't
  // touch it here.
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (
        event.event_type === 'page_builder_chat' &&
        event.page_id === pageId
      ) {
        refreshChat();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, pageId, refreshChat]);

  useEffect(() => {
    if (open) {
      chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chat?.messages?.length, chat?.status, open]);

  const sendChatMessage = async (overrideText?: string) => {
    if (!pageId) return;
    // Quick-prompt chips pass their text directly; the textarea path
    // falls back to the current draft.
    const text = (overrideText ?? chatInput).trim();
    if (!text || sending || chat?.status === 'thinking') return;
    setSending(true);
    try {
      const next = await api.sendPageBuilderChatMessage(pageId, text);
      setChat(next);
      setChatInput('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const startFreshChat = async () => {
    if (!pageId || endingSession) return;
    if (
      !window.confirm(
        'Start a fresh chat?\n\n' +
          '• The current chat is hidden from the LLM and from this view.\n' +
          '• It stays saved in the database (no data is lost).\n' +
          '• The page itself and its build phases are NOT touched.',
      )
    ) {
      return;
    }
    setEndingSession(true);
    try {
      const next = await api.endPageBuilderChatSession(pageId);
      setChat(next);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start fresh chat');
    } finally {
      setEndingSession(false);
    }
  };


  const resetChat = async () => {
    if (!pageId || resetting) return;
    if (
      !window.confirm(
        'Reset the chat?\n\n' +
          '• ALL chat history is wiped.\n' +
          '• The page itself and its build phases are NOT touched.',
      )
    ) {
      return;
    }
    setResetting(true);
    try {
      const next = await api.resetPageBuilderChat(pageId);
      setChat(next);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const clearStuckThinking = async () => {
    if (!pageId) return;
    if (
      !window.confirm(
        'Force-clear the "thinking" state? Use this if the previous request died. Auto-recovery kicks in after ~10 minutes.',
      )
    ) {
      return;
    }
    try {
      const next = await api.clearPageBuilderChatThinking(pageId);
      setChat(next);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to clear');
    }
  };

  return (
    <>
      {/* Backdrop — click to close. Matches the slide-out overlay pattern
          used elsewhere in the admin (SerpModal etc). */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden
      />

      {/* Slide-out panel */}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white dark:bg-gray-800 shadow-xl flex flex-col transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Page builder Ask AI chat"
      >
        {/* Header — title + model chip + status + close */}
        <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
            Ask AI
            {chat && chat.current_session_number > 1 && (
              <span
                className="ml-1.5 text-[11px] font-normal text-gray-500 dark:text-gray-400"
                title="Past sessions stay in the database but don't reach the LLM."
              >
                · session {chat.current_session_number}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {chat && (
              <>
                <button
                  type="button"
                  onClick={startFreshChat}
                  disabled={
                    chat.status === 'thinking' ||
                    endingSession ||
                    chat.messages.length === 0
                  }
                  title="Start over: begins a fresh chat session. Old messages stay in the DB but no longer reach the LLM; the page build is untouched."
                  className="px-2 py-0.5 text-[11px] rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  {endingSession ? 'Starting…' : 'Start over'}
                </button>
                <ContextUsageChip
                  contextTokens={chat.context_tokens}
                  contextWindow={chat.context_window}
                />
                <button
                  type="button"
                  onClick={resetChat}
                  disabled={resetting}
                  title="Wipe ALL chat history for this page. The page build is NOT touched."
                  className="px-2 py-0.5 text-[11px] rounded border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                >
                  {resetting ? 'Resetting…' : 'Reset'}
                </button>
                <StageModelsWidget
                  surface="page_builder_chat"
                  returnLabel="page builder"
                />
                <AgentStatusChip status={chat.status} />
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              title="Close"
              aria-label="Close Ask AI chat"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        {chatLoadError ? (
          <AgentLoadError error={chatLoadError} onRetry={refreshChat} />
        ) : !chat ? (
          <div className="flex-1 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
            Loading chat…
          </div>
        ) : (
          <>
            {chat.status === 'failed' && (
              <AgentErrorBanner error={chat.last_error} stage={chat.agent_stage} />
            )}

            <div className="flex-1 p-3 overflow-y-auto space-y-1.5">
              {chat.messages.length === 0 ? (
                <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                  Ask for research, a reframe, a re-outline, a rewrite, or a
                  fact-check of this page - or just ask what would make it
                  better.
                </div>
              ) : (
                chat.messages.map((m) => <ChatBubble key={m.id} message={m} />)
              )}
              {chat.status === 'thinking' && (
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <span className="italic animate-pulse">AI is working…</span>
                  <Internals>
                    <button
                      type="button"
                      onClick={clearStuckThinking}
                      className="text-indigo-600 dark:text-indigo-400 hover:underline not-italic"
                      title="Force-flip the chat back to idle if it's stuck."
                    >
                      Stuck? Clear
                    </button>
                  </Internals>
                </div>
              )}
              <div ref={chatBottomRef} />
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 p-2">
              <QuickPromptBar
                agentPrompts={chat.suggested_prompts}
                staticPool={staticPageBuilderPrompts(chat.messages.length)}
                page={quickPromptPage}
                onCycle={() => setQuickPromptPage((p) => p + 1)}
                onSend={sendChatMessage}
                disabled={sending || chat.status === 'thinking'}
              />
              <textarea
                rows={3}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask AI - e.g. 'reframe this around beginners' or 'rewrite the intro and re-ground the claims'"
                disabled={sending || chat.status === 'thinking'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendChatMessage();
                  }
                }}
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 dark:bg-gray-800"
              />
              <div className="flex items-center justify-between mt-1.5">
                <div className="text-[10px] text-gray-400 dark:text-gray-500">
                  enter to send · shift+enter for newline
                </div>
                <button
                  type="button"
                  onClick={() => sendChatMessage()}
                  disabled={sending || chat.status === 'thinking' || !chatInput.trim()}
                  className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {sending || chat.status === 'thinking' ? 'Working…' : 'Send'}
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

function ChatBubble({ message }: { message: PageBuilderChatMessage }) {
  const role = (message.entity_type || '').toUpperCase();

  if (role === 'SYSTEM') {
    return (
      <div className="my-1.5 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 rounded p-2 text-xs">
        <div className="flex items-baseline gap-2 mb-1 font-medium text-amber-800 dark:text-amber-300">
          <span>Compacted earlier conversation</span>
          <span className="text-amber-600 dark:text-amber-400 font-normal">
            {_formatChatTime(message.created_at)}
          </span>
        </div>
        <div className="whitespace-pre-wrap break-words text-amber-900 dark:text-amber-200 italic">
          {message.content || '(empty summary)'}
        </div>
      </div>
    );
  }

  const isHuman = role === 'HUMAN';
  const isToolOnly =
    !isHuman && !message.content?.trim() && message.has_tool_call;

  // Tool-only assistant turn → compact action stripe instead of a full
  // bubble. The concierge triggered a phase re-run; the stepper reflects
  // it via its own events. A "(no content)" bubble doesn't earn the same
  // visual weight as a real reply.
  if (isToolOnly) {
    return (
      <div className="flex items-baseline gap-1.5 text-[11px] text-indigo-700 dark:text-indigo-300 italic px-1 py-0.5">
        <span aria-hidden>↻</span>
        <span className="flex-1">AI triggered a build action</span>
        <span className="text-gray-400 dark:text-gray-500 not-italic shrink-0">
          {_formatChatTime(message.created_at)}
        </span>
      </div>
    );
  }

  const bubbleClass = isHuman
    ? 'bg-indigo-600 text-white rounded-br-sm'
    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-sm';
  const labelClass = isHuman ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400';
  return (
    <div className={`flex ${isHuman ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[90%] rounded-lg px-2 py-1.5 text-sm shadow-sm ${bubbleClass}`}>
        <div className={`flex items-baseline gap-2 mb-0.5 text-[10px] font-medium ${labelClass}`}>
          <span>{isHuman ? 'You' : 'AI'}</span>
          <span className="opacity-70">{_formatChatTime(message.created_at)}</span>
        </div>
        {message.content ? (
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
        ) : (
          <div className={`text-xs italic ${isHuman ? 'text-indigo-200' : 'text-gray-500 dark:text-gray-400'}`}>
            (no reply)
          </div>
        )}
        {message.has_tool_call && !isHuman && (
          <div className="text-[10px] text-indigo-700 dark:text-indigo-300 italic mt-1">
            ↻ triggered a build action
          </div>
        )}
      </div>
    </div>
  );
}

// Static quick-prompt fallback, picked by where the user is in the flow.
// Used only when the agent hasn't proposed its own follow-ups for the
// latest turn (see PageBuilderChatResponse.suggested_prompts). The
// concierge orchestrates build phases, so these drive the build forward
// rather than add/curate items.
function staticPageBuilderPrompts(messageCount: number): string[] {
  if (messageCount === 0) {
    // Cold start — nothing built yet.
    return [
      "I'm not sure where to start - what should we do first for this page?",
      'Set the audience and intent before we build',
      'Run the research phase to get started',
      "What's the best angle for this page?",
      'Show me the current build state',
      'Who are we writing this for?',
    ];
  }
  return [
    'Run the next phase',
    "What's blocking this page?",
    'Show me the current build state',
    'Revise the outline',
    'Re-ground the claims against sources',
    'Tighten the framing - audience and intent',
  ];
}

function _formatChatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}
